import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { UserRole } from '../shared/user-roles';
import { tap, catchError, throwError, interval, Subscription, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { jwtDecode } from "jwt-decode";
import { RolesService } from '../services/roles.service';
//const jwt_decode = decode as unknown as (token: string) => any;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private refreshSubscription: Subscription | null = null;
  private baseUrl = environment.apiUrl;
  private modalTrigger: (() => void) | null = null;
  private hasSessionPopupShown = false;
  constructor(private http: HttpClient, private router: Router, private roleService: RolesService) {}

  // 🧾 Registration API
  register(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/register.php`, data);
  }

  // 🔐 Login API
  login(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/login.php`, data);
  }

  // ✅ Save login tokens
  handleLoginSuccess(token: string, refreshToken: string): void {
    localStorage.setItem('authToken', token);
    localStorage.setItem('refreshToken', refreshToken);
    this.startTokenMonitor();
  }

  // 🔐 Get token from localStorage
  getAccessToken(): string | null {
    return localStorage.getItem('authToken');
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  // 🔍 Decode JWT token
  decodeToken(token: string): any {
    try {
      return jwtDecode(token); // ✅ No TS error now
    } catch (err) {
      //console.error('[decodeToken] ❌ Failed to decode token:', err);
      return null;
    }
  }

  // ❌ Check if token expired
  isTokenExpired(token: string): boolean {
    const decoded = this.decodeToken(token);
    if (!decoded?.exp) return true;
    return Date.now() > decoded.exp * 1000;
  }

  // ⏱️ Check if near expiry (2 mins left)
  isAccessTokenNearExpiry(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      //console.log('[isAccessTokenNearExpiry] ❌ No token found');
      return true;
    }

    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp) {
      //console.log('[isAccessTokenNearExpiry] ❌ Decoding failed or exp missing');
      return true;
    }

    const timeLeft = decoded.exp * 1000 - Date.now();
    const secondsLeft = Math.floor(timeLeft / 1000);

    //console.log(`[isAccessTokenNearExpiry] ⏳ Token expires in: ${secondsLeft}s`);

    const isNearExpiry = timeLeft < 60 * 1000;
    //console.log(`[isAccessTokenNearExpiry] 🔍 Near expiry? ${isNearExpiry}`);

    return isNearExpiry;
  }


  // 🔁 Refresh token API call
  refreshToken(): void {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return this.logout();

    this.http.post<any>(`${this.baseUrl}/refresh-token.php`, { refreshToken }).subscribe({
      next: (res) => {
        localStorage.setItem('authToken', res.token);
        if (res.refreshToken) {
          localStorage.setItem('refreshToken', res.refreshToken);
        }

        this.hasSessionPopupShown = false; // 🔁 Reset flag so next time popup can show
      },
      error: () => this.logout()
    });
  }

  setModalTrigger(trigger: () => void) {
    this.modalTrigger = trigger;
  }
  // 🕒 Monitor token every 30s
  // startTokenMonitor() {
  //   if (this.refreshSubscription) return;

  //   this.refreshSubscription = interval(30_000).subscribe(() => {
  //     if (this.isAccessTokenNearExpiry() && this.modalTrigger) {
  //       this.modalTrigger();
  //     }
  //   });
  // }
  startTokenMonitor() {
    if (this.refreshSubscription) return;

    this.refreshSubscription = interval(30_000).subscribe(() => {
      const isNearExpiry = this.isAccessTokenNearExpiry();

      console.log('Token check: near expiry?', isNearExpiry, 'already shown?', this.hasSessionPopupShown);

      if (isNearExpiry && !this.hasSessionPopupShown && this.modalTrigger) {
        this.hasSessionPopupShown = true;
        this.modalTrigger();
      }
    });
  }
  stopTokenMonitor() {
    this.refreshSubscription?.unsubscribe();
    this.refreshSubscription = null;
  }

  // ✅ Is user logged in
  isLoggedIn(): boolean {
    const token = this.getAccessToken();
    return token != null && !this.isTokenExpired(token);
  }

  // 🚪 Logout user
  
  logout() {
    this.hasSessionPopupShown = false;
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('permissions');
    this.stopTokenMonitor();
    this.router.navigate(['/login']);
  }

  // 📧 Get user id from token
  getUserName(): string | null {
    const token = this.getAccessToken();
    const decoded = token ? this.decodeToken(token) : null;
    return decoded?.name || null;
  }

  // 📧 Get user id from token
  getUserId(): string | null {
    const token = this.getAccessToken();
    const decoded = token ? this.decodeToken(token) : null;
    return decoded?.user_id || null;
  }

  // 📧 Get email from token
  getUserEmail(): string | null {
    const token = this.getAccessToken();
    const decoded = token ? this.decodeToken(token) : null;
    return decoded?.email || null;
  }

  // 👮‍♂️ Optional: Get role (if added in backend payload)
  getUserRole(): string | null {
    if(this.isProductOwnerAccess())
      return UserRole.ProductOwner;
    
    const token = this.getAccessToken();
    const decoded = token ? this.decodeToken(token) : null;
    return decoded?.role || null;
  }
  getUserRoleId(): number | null {
    
    const token = this.getAccessToken();
    const decoded = token ? this.decodeToken(token) : null;
    return decoded?.roleId || null;
  }
  // 👮‍♂️ Optional: Get company id (if added in backend payload)
  getUserCompanyId(): string | null {
    const token = this.getAccessToken();
    const decoded = token ? this.decodeToken(token) : null;
    return decoded?.company_id || null;
  }
  // Get current user company plan 
  getCompanyPlan(): string | null {
    const token = this.getAccessToken();
    const decoded = token ? this.decodeToken(token) : null;
    return decoded?.plan_type || null;
  }

  // Get current user company plan status
  getCompanyPlanStatus(): string | null {
    const token = this.getAccessToken();
    const decoded = token ? this.decodeToken(token) : null;
    return decoded?.plan_status || null;
  }

  /** Company-level SOP AI customization (discovery flow) enabled */
  isCompanyCustomizeEnabled(): boolean {
    const token = this.getAccessToken();
    const decoded = token ? this.decodeToken(token) : null;
    return Number(decoded?.customize) === 1;
  }

  hasEditorOrAdminAccess(): boolean {
    const role = this.getUserRole();
    return role === UserRole.SystemAdmin || role === UserRole.Editor;
  }

  isProductOwnerAccess(): boolean {
    const token = this.getAccessToken();
    const decoded = token ? this.decodeToken(token) : null;
    return decoded?.isProductOwner || null;
  }

  isSystemAdmin(): boolean {
    const role = this.getUserRole();
    return role === UserRole.SystemAdmin; 
  }

  // fetchPermissionsAndStore(roleId: number): void {
  //   this.roleService.getRolePermissions({ role_id: roleId }).subscribe({
  //     next: (permrec: any) => {
  //       debugger
  //       this.setUserPermissions(permrec); // same as login
  //     },
  //     error: (err) => {
  //       console.error('Failed to load permissions', err);
  //     }
  //   });
  // }

  fetchPermissionsAndStore(roleId: number): Observable<any> {
    return this.roleService.getRolePermissions({ role_id: roleId }).pipe(
      tap((permrec: any) => {
        this.setUserPermissions(permrec); // same as login
      }),
      catchError((err) => {
        console.error('Failed to load permissions', err);
        return throwError(() => err);
      })
    );
  }

  setUserPermissions(data: any): void {
    localStorage.setItem('permissions', JSON.stringify(data.permissions));
  }

  getLoggedInUserPermissions(): string[] {
    const saved = localStorage.getItem('permissions');
    return saved ? JSON.parse(saved) : [];
  }

  hasPermission(permission?: string): boolean {
    
    if(this.isSystemAdmin())
    {
      return true;  
    }

    if (!permission) return false;

    
    const permissions = this.getLoggedInUserPermissions() || [];
    return permissions.includes(permission);
  }

  clearPermissions(): void {
    localStorage.removeItem('permissions');
  }
}
